import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';
import * as AiIcons from 'react-icons/ai';
import * as IoIcons from 'react-icons/io5';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiPhone, FiCheck, FiX, FiArrowRight, FiStar, FiUsers, FiTrendingUp, FiClock, FiTarget, FiZap, FiShield, FiAward, FiMessageCircle, FiDollarSign, FiBarChart3, FiPlayCircle, FiCalendar, FiRefreshCw } = FiIcons;
const { BiRocket, BiTrophy, BiGroup, BiHeart, BiSupport } = BiIcons;
const { AiFillStar } = AiIcons;
const { IoCheckmarkCircle, IoCloseCircle, IoSparkles, IoFlash } = IoIcons;

function App() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleBooking = () => {
    window.open("https://dmc.yinguru.com/widget/booking/mESB0fo9Nb1gpw4V1EDs", "_blank", "noopener,noreferrer");
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-orange-500/20"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width=%2260%22%20height=%2260%22%20viewBox=%220%200%2060%2060%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill=%22none%22%20fill-rule=%22evenodd%22%3E%3Cg%20fill=%22%23ffffff%22%20fill-opacity=%220.05%22%3E%3Ccircle%20cx=%2230%22%20cy=%2230%22%20r=%224%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <motion.div 
            className="text-center max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Trust Badges */}
            <motion.div 
              className="flex justify-center items-center space-x-2 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center space-x-1">
                {[1,2,3,4,5].map((star) => (
                  <AiFillStar key={star} className="text-yellow-400 text-xl" />
                ))}
              </div>
              <span className="text-white/80 text-sm">Trusted by 500+ Dojos</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Grow Your Karate Academy to{' '}
              <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                100+ Students Monthly
              </span>{' '}
              Using AI Funnels & WhatsApp Automation
            </motion.h1>

            <motion.div 
              className="text-xl text-red-500 font-bold mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              — Without Hiring Any Staff!
            </motion.div>

            {/* Sub-headline */}
            <motion.p 
              className="text-xl md:text-2xl text-white/90 mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Build a powerful student admission system that runs 24/7, fills your classes, increases retention, and grows your dojo using intelligent automation — even while you teach.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <button onClick={handleBooking} className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-6 px-12 rounded-full text-xl shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-3 mx-auto">
                <SafeIcon icon={FiPhone} className="text-2xl" />
                <span>Book a Free Strategy Call</span>
                <SafeIcon icon={FiArrowRight} className="text-xl" />
              </button>
            </motion.div>

            {/* Hero Visual Elements */}
            <motion.div 
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
              variants={staggerContainer}
              animate="animate"
            >
              <motion.div variants={fadeInUp} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <SafeIcon icon={BiRocket} className="text-4xl text-red-500 mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">AI Automation</h3>
                <p className="text-white/80">24/7 lead capture & follow-up</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <SafeIcon icon={FiMessageCircle} className="text-4xl text-orange-500 mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">WhatsApp Bot</h3>
                <p className="text-white/80">Instant parent responses</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <SafeIcon icon={BiTrophy} className="text-4xl text-yellow-500 mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">Proven Results</h3>
                <p className="text-white/80">300% more admissions</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-800">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              The Real Struggles Karate Coaching Institutes Face Today
            </h2>
            <p className="text-xl text-red-400 font-semibold mb-12">
              "You became a Sensei to teach karate… not to chase leads, messages, and marketing."
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { icon: FiTrendingUp, text: "Difficulty finding new students consistently", color: "text-red-500" },
              { icon: FiMessageCircle, text: "Parents enquiring but never responding again", color: "text-red-500" },
              { icon: FiUsers, text: "Zero automation — everything depends on the owner", color: "text-red-500" },
              { icon: FiClock, text: "No time to manage marketing, leads, or nurturing", color: "text-red-500" },
              { icon: FiTarget, text: "Competition increasing every month", color: "text-red-500" },
              { icon: FiBarChart3, text: "Admissions are seasonal, unpredictable, and unstable", color: "text-red-500" },
              { icon: FiRefreshCw, text: "Poor follow-up = 80% leads lost", color: "text-red-500" },
              { icon: BiGroup, text: "Students drop out after 2–3 months", color: "text-red-500" },
              { icon: FiPhone, text: "Manual WhatsApp messaging consumes hours", color: "text-red-500" }
            ].map((problem, index) => (
              <motion.div
                key={index}
                className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-red-500/20"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={problem.icon} className={`text-2xl ${problem.color} flex-shrink-0 mt-1`} />
                  <p className="text-white/90 leading-relaxed">{problem.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Your All-In-One AI Powered Student Admission System
            </h2>
            <p className="text-xl text-white/80 max-w-4xl mx-auto">
              FunnelsDone & AIFunnels automate EVERYTHING — enquiries, follow-ups, demos, payments, attendance, reminders, nurturing, and upsells.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {[
              {
                icon: FiTarget,
                title: "Instant Lead Capture Funnels",
                description: "High-converting landing pages designed for karate admissions.",
                color: "from-red-500 to-red-600"
              },
              {
                icon: FiMessageCircle,
                title: "WhatsApp Auto-Follow-ups",
                description: "No missed leads — every parent receives instant replies + nurturing sequences.",
                color: "from-green-500 to-green-600"
              },
              {
                icon: FiCalendar,
                title: "Automated Free Demo Class System",
                description: "Book, confirm, remind, and follow up — 100% automated.",
                color: "from-blue-500 to-blue-600"
              },
              {
                icon: BiRocket,
                title: "AI Chatbot for Parent Queries",
                description: "24/7 instant answers about fees, timings, batches, instructors, belt levels, etc.",
                color: "from-purple-500 to-purple-600"
              },
              {
                icon: BiHeart,
                title: "Student Retention Automation",
                description: "Weekly progress messages, motivational updates, attendance reminders, renewal alerts.",
                color: "from-pink-500 to-pink-600"
              },
              {
                icon: FiDollarSign,
                title: "Payment & Fee Collection Funnels",
                description: "Simple payment links, automated receipts, renewal reminders.",
                color: "from-yellow-500 to-yellow-600"
              },
              {
                icon: BiTrophy,
                title: "Upsell Funnels",
                description: "Belt ceremony offers, summer camp funnels, self-defence workshop funnels.",
                color: "from-orange-500 to-orange-600"
              },
              {
                icon: FiBarChart3,
                title: "Full Marketing Automation Suite",
                description: "Email, WhatsApp, SMS, funnel analytics, lead tracking.",
                color: "from-indigo-500 to-indigo-600"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={feature.icon} className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-white/70 leading-relaxed">{feature.description}</p>
                <div className="mt-4 flex items-center text-green-400">
                  <SafeIcon icon={FiCheck} className="mr-2" />
                  <span className="text-sm font-medium">Included</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="py-20 bg-slate-800">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-12">
              What Happens When a Karate Institute Uses FunnelsDone
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: FiTrendingUp,
                title: "More Admissions Every Month",
                description: "Consistent flow of new students joining your dojo",
                color: "text-green-400"
              },
              {
                icon: FiShield,
                title: "Parents Trust Increases",
                description: "Professional automation builds instant credibility",
                color: "text-blue-400"
              },
              {
                icon: BiHeart,
                title: "Kids Stay Longer",
                description: "Retention improves with automated engagement",
                color: "text-pink-400"
              },
              {
                icon: FiDollarSign,
                title: "Stable Monthly Revenue",
                description: "Recurring income becomes predictable and grows",
                color: "text-yellow-400"
              },
              {
                icon: FiTarget,
                title: "Focus on Teaching",
                description: "Sensei can focus on teaching, not marketing",
                color: "text-red-400"
              },
              {
                icon: BiRocket,
                title: "Stress-Free Growth",
                description: "Your dojo grows without overwhelming stress",
                color: "text-purple-400"
              },
              {
                icon: FiUsers,
                title: "Quality Students",
                description: "Attract serious students instead of time-wasters",
                color: "text-orange-400"
              },
              {
                icon: FiAward,
                title: "Professional Brand",
                description: "Become the go-to karate academy in your area",
                color: "text-indigo-400"
              }
            ].map((transformation, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-slate-700/50 to-slate-600/30 backdrop-blur-sm rounded-xl p-6 text-center border border-white/10"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <SafeIcon icon={transformation.icon} className={`text-4xl ${transformation.color} mx-auto mb-4`} />
                <h3 className="text-lg font-bold text-white mb-3">{transformation.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{transformation.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before vs After Comparison */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-4xl md:text-5xl font-black text-white text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            The Complete Transformation
          </motion.h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Before */}
            <motion.div 
              className="bg-red-900/20 border border-red-500/30 rounded-2xl p-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">Before FunnelsDone</h3>
              <div className="space-y-4">
                {[
                  "Struggling with admissions",
                  "Manually replying to every parent",
                  "No follow-up system",
                  "Low conversion rates",
                  "No proper automation",
                  "High dropout rates",
                  "Zero brand authority",
                  "Unpredictable revenue"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiX} className="text-red-500 flex-shrink-0" />
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* After */}
            <motion.div 
              className="bg-green-900/20 border border-green-500/30 rounded-2xl p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-green-400 mb-6 text-center">After FunnelsDone</h3>
              <div className="space-y-4">
                {[
                  "24/7 automated lead capture",
                  "Instant WhatsApp replies",
                  "Automated demo class booking",
                  "Smart follow-up reminders",
                  "High conversion rates",
                  "Parents trust you instantly",
                  "Complete marketing automation",
                  "Professional brand image"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="text-green-500 flex-shrink-0" />
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* What You Get */}
            <motion.div 
              className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-2xl p-8"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold text-blue-400 mb-6 text-center">What You Get</h3>
              <div className="space-y-4">
                {[
                  "Ready-made funnels",
                  "WhatsApp AI system",
                  "CRM dashboard",
                  "Automation workflows",
                  "Templates for ads & messages",
                  "Unlimited support",
                  "Lifetime updates",
                  "Dedicated onboarding"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={IoSparkles} className="text-yellow-400 flex-shrink-0" />
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Karate-Specific Solutions */}
      <section className="py-20 bg-slate-800">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Why This Is Designed SPECIFICALLY for Karate Coaching Centres
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: "Kids Admission Funnels", icon: BiGroup, description: "Age-specific landing pages for different belt levels" },
              { title: "Adult Self-Defence Batch Funnels", icon: FiShield, description: "Targeted campaigns for working professionals" },
              { title: "Summer Camp Funnels", icon: FiCalendar, description: "Seasonal programs with automated enrollment" },
              { title: "Black Belt Batch Leads", icon: FiAward, description: "Premium programs for advanced students" },
              { title: "Demo Class Automation", icon: FiPlayCircle, description: "Free trial class booking and follow-up" },
              { title: "Instructor Branding Funnels", icon: BiTrophy, description: "Build authority for your sensei team" },
              { title: "Parent Nurturing Messages", icon: BiHeart, description: "Keep parents engaged and informed" },
              { title: "Progress Tracking Messages", icon: FiTrendingUp, description: "Automated updates on student development" },
              { title: "Renewal Funnels", icon: FiRefreshCw, description: "Automatic membership renewals and upgrades" }
            ].map((solution, index) => (
              <motion.div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-orange-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <SafeIcon icon={solution.icon} className="text-3xl text-orange-500 mb-4" />
                <h3 className="text-lg font-bold text-white mb-3">{solution.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{solution.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to 10X Your Admissions and Build a Full Dojo?
            </h2>
            <p className="text-xl text-orange-400 font-semibold">
              "One small monthly fee replaces 5 staff members."
            </p>
          </motion.div>

          <motion.div 
            className="max-w-4xl mx-auto bg-gradient-to-br from-slate-700/50 to-slate-600/30 backdrop-blur-sm rounded-3xl p-12 border border-white/10"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-white text-center mb-8">Complete AI Automation System</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {[
                { icon: BiRocket, title: "AI Chatbot", description: "24/7 parent query responses" },
                { icon: FiMessageCircle, title: "WhatsApp Automation", description: "Instant follow-ups & nurturing" },
                { icon: FiTarget, title: "5+ Ready Funnels", description: "Kids, adults, camps, renewals" },
                { icon: BiHeart, title: "Student Retention Automations", description: "Keep students engaged longer" },
                { icon: BiSupport, title: "Dedicated Onboarding", description: "Complete setup & training" },
                { icon: FiZap, title: "Unlimited Support", description: "Always here to help you grow" }
              ].map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <SafeIcon icon={feature.icon} className="text-2xl text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-bold mb-1">{feature.title}</h4>
                    <p className="text-white/70 text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <button onClick={handleBooking} className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-bold py-6 px-12 rounded-full text-xl shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-3 mx-auto">
                <SafeIcon icon={FiPhone} className="text-2xl" />
                <span>Get Your Free Strategy Call</span>
                <SafeIcon icon={FiArrowRight} className="text-xl" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-800">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-4xl md:text-5xl font-black text-white text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "Do I need technical skills to use this system?",
                answer: "Not at all! We handle the complete setup and provide full training. You just need to focus on teaching karate."
              },
              {
                question: "How fast can I get results?",
                answer: "Most dojos see increased inquiries within the first week and 2-3x more admissions within 30 days."
              },
              {
                question: "Is this suitable for small dojos?",
                answer: "Absolutely! Whether you have 20 students or 200, our system scales with your dojo and helps you grow systematically."
              },
              {
                question: "Will you help with the complete setup?",
                answer: "Yes! We provide dedicated onboarding, complete setup, and ongoing support to ensure your success."
              },
              {
                question: "Can I integrate this with my existing website?",
                answer: "Yes! Our funnels can be integrated with any existing website or work as standalone landing pages."
              },
              {
                question: "Is WhatsApp automation really included?",
                answer: "Yes! Complete WhatsApp automation including chatbot, follow-up sequences, and parent nurturing messages."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-xl font-bold text-white mb-3">{faq.question}</h3>
                <p className="text-white/80 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-orange-600">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-8 leading-tight">
              You are not just teaching karate.
            </h2>
            <p className="text-2xl text-white/90 mb-4">
              You are shaping discipline, focus, courage, and confidence in students.
            </p>
            <p className="text-xl text-white/80 mb-12 max-w-3xl mx-auto">
              Let us help you fill your dojo with the students who need you.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto mb-12">
              <h3 className="text-2xl font-bold text-white mb-4">
                Get a FREE 30-Minute Growth Blueprint for Your Karate Academy
              </h3>
              <p className="text-red-200 font-semibold mb-6">— Limited Slots Only —</p>
              <p className="text-white/90 mb-8">Only 5 Dojos accepted this month.</p>
              
              <button onClick={handleBooking} className="bg-white text-red-600 hover:bg-gray-100 font-bold py-6 px-12 rounded-full text-xl shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-3 mx-auto">
                <SafeIcon icon={IoFlash} className="text-2xl" />
                <span>Book Your Free Strategy Call</span>
                <SafeIcon icon={FiArrowRight} className="text-xl" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-red-600 to-orange-600 p-4 z-50 md:hidden">
        <button onClick={handleBooking} className="w-full bg-white text-red-600 font-bold py-4 px-6 rounded-full text-lg shadow-lg flex items-center justify-center space-x-2">
          <SafeIcon icon={FiPhone} className="text-xl" />
          <span>Book Your Free Strategy Call Now</span>
        </button>
      </div>
    </div>
  );
}

export default App;